'use client'

import { type FC, useCallback } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { useDirectContactFormPrefillCustomerData } from '../../hooks/useDirectContactFormPrefillCustomerData'
import { useDirectContactFormSubjectLoginModal } from '../../hooks/useDirectContactFormSubjectLoginModal'
import { useDirectContactFormSubmit } from '../../hooks/useDirectContactFormSubmit'
import { useDirectContactFormSubmitFullStoryTracking } from '../../hooks/useDirectContactFormSubmitFullStoryTracking'
import { useOpenContactFormFromSearchParam } from '../../hooks/useOpenContactFormFromSearchParam'
import { CONTACT_FORM_SUBJECT_KEY } from '../../model/directContactFormSubjectCatalog'
import { directContactFormSubjectPolicyGetByTranslatedSubject } from '../../model/directContactFormSubjectPolicySelectors'

import { DirectContactFormHookFormDialogContent } from './DirectContactFormHookFormDialogContent'
import { DirectContactFormHookFormDialogAuthenticationProvider } from './DirectContactFormHookFormDialogContext'
import {
  DIRECT_CONTACT_FORM_HOOK_FORM_DIALOG_DEFAULT_VALUES,
  type DirectContactFormHookFormDialogProps,
  type DirectContactFormHookFormDialogValues,
} from './DirectContactFormHookFormDialogModel'

const getFormDefaultValues = ({
  formSubject,
  isCancelContract,
  prefilledSubOrder,
  product,
}: Pick<
  DirectContactFormHookFormDialogProps,
  'formSubject' | 'prefilledSubOrder' | 'product'
> & { isCancelContract: boolean }): DirectContactFormHookFormDialogValues => ({
  ...DIRECT_CONTACT_FORM_HOOK_FORM_DIALOG_DEFAULT_VALUES,
  message: isCancelContract ? (formSubject ?? '') : '',
  orderId: prefilledSubOrder?.subOrderId ?? '',
  productCode: product ? `${product.upid} - ${product.name}` : '',
  subject: formSubject ?? '',
})

export const DirectContactFormHookFormDialog: FC<
  DirectContactFormHookFormDialogProps
> = ({
  contactFormLaunchOrigin,
  disableSubject,
  formSubject,
  isSubjectPharmaRelated,
  modalControls,
  onClose,
  onSuccess,
  onSuccessClose,
  prefilledSubOrder,
  product,
}) => {
  const intl = useIntl()
  const isCancelContract =
    formSubject !== undefined &&
    directContactFormSubjectPolicyGetByTranslatedSubject({
      intl,
      subject: formSubject,
    }).key === CONTACT_FORM_SUBJECT_KEY.CANCEL_CONTRACT
  const formMethods = useForm<DirectContactFormHookFormDialogValues>({
    defaultValues: getFormDefaultValues({
      formSubject,
      isCancelContract,
      prefilledSubOrder,
      product,
    }),
    mode: 'onBlur',
  })
  const { control, reset, resetField, setValue } = formMethods
  const selectedSubject = useWatch({ control, name: 'subject' })
  const { isCustomerDataComplete, isCustomerLoggedIn } =
    useDirectContactFormPrefillCustomerData(setValue)
  const {
    handleLoginModalClose,
    handleLoginSuccess,
    handleOrderRequestAuthenticationError,
    isLoggedIn,
    loginModalControls,
  } = useDirectContactFormSubjectLoginModal({
    hasPrefilledOrder: Boolean(prefilledSubOrder),
    intl,
    isContactFormOpen: modalControls.isOpen,
    isCustomerLoggedIn,
    selectedSubject,
  })
  const trackSuccessfulSubmit = useDirectContactFormSubmitFullStoryTracking({
    contactFormLaunchOrigin,
    formSubject,
  })
  const handleSubmittedWithSuccess = useCallback(() => {
    reset()
    onSuccess?.()
    trackSuccessfulSubmit()
  }, [onSuccess, reset, trackSuccessfulSubmit])
  const handleAuthenticationError = useCallback((): void => {
    resetField('fcSolution')
    handleOrderRequestAuthenticationError()
  }, [handleOrderRequestAuthenticationError, resetField])
  const { isMutating, onSubmit, resetSubmitStatus, submitStatus } =
    useDirectContactFormSubmit({
      onAuthenticationError: handleAuthenticationError,
      onSuccess: handleSubmittedWithSuccess,
    })
  const handleClose = useCallback((): void => {
    resetSubmitStatus()
    reset()
    modalControls.handleClose()
    onClose?.()
  }, [modalControls, onClose, reset, resetSubmitStatus])
  const handleSuccessNotificationClose = useCallback((): void => {
    resetSubmitStatus()
    modalControls.handleClose()
  }, [modalControls, resetSubmitStatus])
  useOpenContactFormFromSearchParam(modalControls.handleOpen)

  return (
    <FormProvider {...formMethods}>
      <DirectContactFormHookFormDialogAuthenticationProvider
        handleLoginModalClose={handleLoginModalClose}
        handleLoginSuccess={handleLoginSuccess}
        isCustomerDataComplete={isCustomerDataComplete}
        isLoggedIn={isLoggedIn}
        isLoginModalOpen={loginModalControls.isOpen}
      >
        <DirectContactFormHookFormDialogContent
          configuration={{
            disableSubject,
            isSubjectPharmaRelated,
            prefilledSubOrder,
            product,
            resolvedSubject:
              directContactFormSubjectPolicyGetByTranslatedSubject({
                intl,
                subject: selectedSubject,
              }),
          }}
          contactFormLaunchOrigin={contactFormLaunchOrigin}
          handleClose={handleClose}
          handleSuccessNotificationClose={handleSuccessNotificationClose}
          isCancelContract={isCancelContract}
          isMutating={isMutating}
          isOpen={modalControls.isOpen}
          onSubmit={onSubmit}
          onSuccessClose={onSuccessClose}
          submitStatus={submitStatus}
        />
      </DirectContactFormHookFormDialogAuthenticationProvider>
    </FormProvider>
  )
}
